'use client'
import { Stack, Container, Box } from '@mui/material'
import { styled } from '@mui/material/styles'
import LanguageIcon from '@mui/icons-material/Language'
import { useContext } from 'react'
import { useTranslation } from 'react-i18next'

import { getLocaleName } from '@/utils/locale'
import LocaleContext from '@/providers/context/LocaleContext'
import Link from '@/components/common/Link'

const base = {
  fontWeight: 'initial',
  fontStretch: 'initial',
  fontSize: '0.65em'
}

const FooterLink = styled(Link)(({ theme }) => ({
  ...base,
  textTransform: 'none',
  textDecoration: 'none',
  color: `${theme.palette.text.primary} !important`,
  letterSpacing: 'inherit',
  fontStretch: 'initial',
  '&:hover, &:focus, &:focus-visible': {
    color: `${theme.palette.primary.main} !important`,
    backgroundColor: 'transparent'
  }
}))

export default function FooterLogosLinks() {
  const { t } = useTranslation()
  const { locale: currentLocale } = useContext(LocaleContext)

  return (
    <Container
      maxWidth={false}
      role='navigation'
      aria-label={t('Microsoft corporate links')}
      sx={{
        px: { xs: 0 },
        pb: { xs: 12, md: 3 }
      }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent={{ xs: 'space-between' }}
        alignItems={'center'}
        spacing={4}
        sx={{
          pt: { xs: 2, md: 3 },
          px: { xs: 2, md: 2.25, lg: 8 }
        }}>
        <Stack
          direction={'row'}
          alignItems={'center'}
          sx={{ width: '100%', justifyContent: { xs: 'space-between', md: 'flex-start' }, gap: { md: 3 } }}>
          <FooterLink
            href='/locales/'
            title={t('Change locale')}
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, p: 0 }}
            aria-label={t('slug:current-language-lang-go-to-locales-page', { lang: getLocaleName(currentLocale) })}>
            <LanguageIcon sx={{ fontSize: '1.5rem' }} />
            {getLocaleName(currentLocale)}
          </FooterLink>
          <FooterLink href='https://go.microsoft.com/fwlink/?linkid=2259814' target='_blank' rel='noopener noreferrer'>
            {t('Consumer Health Privacy')}
          </FooterLink>
        </Stack>
        <Stack
          direction={'row'}
          alignItems={'center'}
          justifyContent={{ xs: 'space-between', md: 'flex-end' }}
          spacing={{ xs: 0.5, md: 2 }}
          sx={{
            width: {
              xs: '100%'
            }
          }}>
          <FooterLink href='https://go.microsoft.com/fwlink/?LinkId=521839' target='_blank' rel='noopener noreferrer'>
            {t('Privacy & cookies')}
          </FooterLink>
          <FooterLink href='https://www.microsoft.com/trademarks' target='_blank' rel='noopener noreferrer'>
            {t('Trademarks')}
          </FooterLink>
          <FooterLink href='https://www.microsoft.com/servicesagreement' target='_blank' rel='noopener noreferrer'>
            {t('Terms of Use')}
          </FooterLink>
          {currentLocale === 'fr-fr' && (
            <FooterLink href='https://go.microsoft.com/fwlink/?linkid=2121428' target='_blank' rel='noopener noreferrer'>
              {t('Accessibility')}
            </FooterLink>
          )}
          <Box
            component='span'
            sx={{
              fontSize: base.fontSize,
              cursor: 'default'
            }}>
            © {t('Microsoft')} {new Date().getFullYear()}
          </Box>
        </Stack>
      </Stack>
    </Container>
  )
}
